'use client'

import { AnimatePresence, m } from 'framer-motion'
import { ChevronDown, Target } from 'lucide-react'
import type { RefObject } from 'react'

import { FINAL_CTA_GOALS } from '@/components/sections/final-cta/model'

type FinalCtaGoalSelectProps = {
  dropdownRef: RefObject<HTMLDivElement | null>
  value: string
  isOpen: boolean
  onToggle: () => void
  onSelect: (goal: string) => void
}

export function FinalCtaGoalSelect({
  dropdownRef,
  value,
  isOpen,
  onToggle,
  onSelect,
}: FinalCtaGoalSelectProps) {
  const selectedGoal = FINAL_CTA_GOALS.find((goal) => goal.value === value)

  return (
    <div ref={dropdownRef}>
      <label className="mb-2 block text-sm text-gray-400">Цель обращения *</label>
      <div className="relative">
        <button
          type="button"
          onClick={onToggle}
          className={`flex w-full items-center justify-between rounded-2xl border bg-white/5 py-4 pr-4 pl-12 text-left transition-all ${
            isOpen ? 'border-blue-500/50 shadow-[0_0_20px_rgba(99,102,241,0.3)]' : 'border-white/10'
          }`}
        >
          <Target
            className={`absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2 transition-colors ${isOpen ? 'text-blue-400' : 'text-gray-500'}`}
          />
          <span className={selectedGoal ? 'text-white' : 'text-gray-400'}>
            {selectedGoal?.label || 'Выберите цель'}
          </span>
          <m.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
            <ChevronDown className="h-5 w-5 text-gray-500" />
          </m.div>
        </button>

        <AnimatePresence>
          {isOpen ? (
            <m.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="absolute top-full right-0 left-0 z-50 mt-2 overflow-hidden rounded-2xl border border-white/10 bg-[#0a0f1a] shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
            >
              {FINAL_CTA_GOALS.map((goal, index) => (
                <m.button
                  key={goal.value}
                  type="button"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => onSelect(goal.value)}
                  className={`w-full px-4 py-3.5 text-left transition-all ${
                    value === goal.value
                      ? 'bg-blue-500/20 text-blue-400'
                      : 'text-gray-300 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  {goal.label}
                </m.button>
              ))}
            </m.div>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  )
}
